import i18n from '../i18n';

// Dinamik API URL - production'da her zaman same-origin (/api via nginx)
const getApiBaseUrl = () => {
  const { protocol, hostname, port } = window.location;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_URL || 'http://localhost:7070/api';
  }

  // Deployed environments: never trust a stale absolute REACT_APP_API_URL
  return `${protocol}//${hostname}${port ? ':' + port : ''}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// Logout ve redirect helper
const handleUnauthorized = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('isCompanyAdmin');
  localStorage.removeItem('isSystemAdmin');
  
  // Login sayfasına yönlendir (mevcut sayfa login değilse)
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login?session_expired=true';
  }
};

// API helper function
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // 401 Unauthorized - session expired or invalid token
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error(i18n.t('errors.sessionExpired', 'Session expired. Please login again.'));
    }

    const raw = await response.text();
    let data = null;
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        if (!response.ok) {
          throw new Error(raw.trim() || 'Request failed');
        }
        throw new Error('Invalid server response');
      }
    }

    if (!response.ok) {
      const message =
        (data && (data.error || data.message)) ||
        raw.trim() ||
        'Request failed';
      throw new Error(message);
    }

    return data;
  } catch (error) {
    // Network error veya server ulaşılamıyor
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      throw new Error(i18n.t('errors.serverIssue'));
    }
    throw error;
  }
};

// Auth API
export const signup = async (userData) => {
  const response = await apiCall('/signup', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
  
  if (response.token) {
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    localStorage.setItem(
      'isCompanyAdmin',
      String(!!(response.is_company_admin || response.user?.is_company_admin))
    );
    localStorage.setItem(
      'isSystemAdmin',
      String(!!(response.is_system_admin || response.user?.is_system_admin))
    );
  }
  
  return response;
};

export const login = async (credentials) => {
  const response = await apiCall('/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
  
  // If 2FA is required, don't store anything yet
  if (response.two_factor_required) {
    return response;
  }
  
  if (response.token) {
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    localStorage.setItem(
      'isCompanyAdmin',
      String(!!(response.is_company_admin || response.user?.is_company_admin))
    );
    localStorage.setItem(
      'isSystemAdmin',
      String(!!(response.is_system_admin || response.user?.is_system_admin))
    );
  }
  
  return response;
};

export const verify2FA = async (userId, code) => {
  const response = await apiCall('/verify-2fa', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, code }),
  });
  
  if (response.token) {
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    localStorage.setItem(
      'isCompanyAdmin',
      String(!!(response.is_company_admin || response.user?.is_company_admin))
    );
    localStorage.setItem(
      'isSystemAdmin',
      String(!!(response.is_system_admin || response.user?.is_system_admin))
    );
  }
  
  return response;
};

export const resend2FA = async (userId) => {
  return apiCall('/resend-2fa', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
};

export const verifyEmail = async (code) => {
  return apiCall('/verify-email', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
};

export const resendVerification = async (email) => {
  return apiCall('/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

// Profile API
export const getProfile = async () => {
  return apiCall('/profile', {
    method: 'GET',
  });
};

export const changePassword = async (currentPassword, newPassword, confirmPassword) => {
  return apiCall('/profile/change-password', {
    method: 'POST',
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
  });
};

// User API
export const getCurrentUser = async () => {
  return apiCall('/users/me');
};

// Logs API
export const getLogs = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.level) params.append('level', filters.level);
  if (filters.category) params.append('category', filters.category);
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  
  const queryString = params.toString();
  return apiCall(`/logs${queryString ? `?${queryString}` : ''}`);
};

// User Lookup API (for logs filtering)
export const getUsersLookup = async (searchQuery = '') => {
  const params = searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : '';
  return apiCall(`/users/lookup${params}`);
};

// Company Invites API
export const createInvite = async (inviteData) => {
  return apiCall('/invites', {
    method: 'POST',
    body: JSON.stringify(inviteData),
  });
};

export const getInvites = async () => {
  return apiCall('/invites');
};

// Company Users API
export const getCompanyUsers = async () => {
  return apiCall('/company/users');
};

// Notifications API
export const getNotifications = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`/notifications${queryString ? `?${queryString}` : ''}`);
};

export const getUnreadCount = async () => {
  return apiCall('/notifications/unread-count');
};

export const markNotificationAsRead = async (notificationId) => {
  return apiCall(`/notifications/${notificationId}/read`, {
    method: 'PUT',
  });
};

export const markAllNotificationsAsRead = async () => {
  return apiCall('/notifications/mark-all-read', {
    method: 'PUT',
  });
};

export const deleteNotification = async (notificationId) => {
  return apiCall(`/notifications/${notificationId}`, {
    method: 'DELETE',
  });
};

// VPN API
export const getAvailableNodes = async () => {
  return apiCall('/vpn/nodes');
};

export const getAllNodesWithAccess = async () => {
  return apiCall('/vpn/all-nodes');
};

export const connectToNode = async (nodeId) => {
  return apiCall('/vpn/connect', {
    method: 'POST',
    body: JSON.stringify({ node_id: nodeId }),
  });
};

export const disconnectFromNode = async (connectionId) => {
  return apiCall('/vpn/disconnect', {
    method: 'POST',
    body: JSON.stringify({ connection_id: Number(connectionId) }),
  });
};

export const getUserStats = async () => {
  return apiCall('/vpn/stats');
};

export const getDashboardOverview = async () => {
  return apiCall('/vpn/dashboard');
};

export const getUserConnections = async (limit = 20) => {
  return apiCall(`/vpn/connections?limit=${limit}`);
};

export const getCompanyConnections = async (limit = 50) => {
  return apiCall(`/vpn/company-connections?limit=${limit}`);
};

export const getMyConnectionStats = async () => {
  return apiCall('/vpn/connections/my-stats');
};

export const getCompanyConnectionStats = async () => {
  return apiCall('/vpn/connections/company-stats');
};

export const getCompanyInfo = async () => {
  return apiCall('/company/info');
};

export const companyCheckout = async (planKey) => {
  return apiCall('/company/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan_key: planKey }),
  });
};

export const inviteUser = async (email) => {
  return apiCall('/company/invite', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

export const deleteInvite = async (inviteId) => {
  return apiCall(`/invites/${inviteId}`, {
    method: 'DELETE',
  });
};

// Landing Page API (public - no auth required for GET)
export const getLandingContent = async (language = 'tr') => {
  const response = await fetch(`${API_BASE_URL}/landing`, {
    headers: {
      'Accept-Language': language,
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch landing content');
  }
  
  return response.json();
};

// Landing Page Admin API (System Admin only)
export const updateLandingContent = async (section, key, valueTr, valueEn) => {
  return apiCall('/landing/content', {
    method: 'PUT',
    body: JSON.stringify({ section, key, value_tr: valueTr, value_en: valueEn }),
  });
};

export const createLandingFeature = async (feature) => {
  return apiCall('/landing/features', {
    method: 'POST',
    body: JSON.stringify(feature),
  });
};

export const updateLandingFeature = async (id, feature) => {
  return apiCall(`/landing/features/${id}`, {
    method: 'PUT',
    body: JSON.stringify(feature),
  });
};

export const deleteLandingFeature = async (id) => {
  return apiCall(`/landing/features/${id}`, {
    method: 'DELETE',
  });
};

export const createLandingTestimonial = async (testimonial) => {
  return apiCall('/landing/testimonials', {
    method: 'POST',
    body: JSON.stringify(testimonial),
  });
};

export const updateLandingTestimonial = async (id, testimonial) => {
  return apiCall(`/landing/testimonials/${id}`, {
    method: 'PUT',
    body: JSON.stringify(testimonial),
  });
};

export const deleteLandingTestimonial = async (id) => {
  return apiCall(`/landing/testimonials/${id}`, {
    method: 'DELETE',
  });
};

// Admin Nodes API (System Admin only)
export const getAdminNodes = async () => {
  return apiCall('/admin/nodes', {
    method: 'GET',
  });
};

export const getConnectedAgents = async () => {
  return apiCall('/agent/connected', {
    method: 'GET',
  });
};

export const getPendingNodes = async () => {
  return apiCall('/agent/pending-nodes', {
    method: 'GET',
  });
};

export const approveNode = async (nodeId, data = {}) => {
  return apiCall(`/agent/nodes/${nodeId}/approve`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const rejectNode = async (nodeId) => {
  return apiCall(`/agent/nodes/${nodeId}/reject`, {
    method: 'DELETE',
  });
};

export const toggleNode = async (nodeId, enabled) => {
  return apiCall(`/agent/nodes/${nodeId}/toggle`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
};

export const toggleNodeVisibility = async (nodeId) => {
  return apiCall(`/agent/nodes/${nodeId}/visibility`, {
    method: 'PUT',
  });
};

export const updatePendingNode = async (nodeId, data) => {
  return apiCall(`/agent/nodes/${nodeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const updateNodeSettings = async (nodeId, settings) => {
  return apiCall(`/agent/nodes/${nodeId}/settings`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
};

export const deleteNode = async (nodeId) => {
  return apiCall(`/agent/nodes/${nodeId}`, {
    method: 'DELETE',
  });
};

export const getNodeLicenses = async (nodeId) => {
  return apiCall(`/agent/nodes/${nodeId}/licenses`, {
    method: 'GET',
  });
};

// Location Management API (System Admin only)
export const getLocations = async () => {
  return apiCall('/locations', {
    method: 'GET',
  });
};

export const createLocation = async (location) => {
  return apiCall('/locations', {
    method: 'POST',
    body: JSON.stringify(location),
  });
};

export const updateLocation = async (id, location) => {
  return apiCall(`/locations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(location),
  });
};

export const deleteLocation = async (id) => {
  return apiCall(`/locations/${id}`, {
    method: 'DELETE',
  });
};

export const getAgentRegisterKeys = async () => {
  return apiCall('/agent/register-keys', {
    method: 'GET',
  });
};

export const createAgentRegisterKey = async (description, expires_in_days = 30) => {
  return apiCall('/agent/register-keys', {
    method: 'POST',
    body: JSON.stringify({ description, expires_in_days }),
  });
};

export const revokeAgentRegisterKey = async (keyId) => {
  return apiCall(`/agent/register-keys/${keyId}/revoke`, {
    method: 'POST',
  });
};

export const restartNode = async (nodeId) => {
  return apiCall(`/agent/nodes/${nodeId}/restart`, {
    method: 'POST',
  });
};

export const listEdgeHosts = async () => {
  return apiCall('/admin/edge-hosts', {
    method: 'GET',
  });
};

export const createEdgeHost = async (payload) => {
  return apiCall('/admin/edge-hosts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const redeployEdgeHost = async (hostId, expiresInDays = 30) => {
  return apiCall(`/admin/edge-hosts/${hostId}/redeploy`, {
    method: 'POST',
    body: JSON.stringify({ expires_in_days: expiresInDays }),
  });
};

export const getActiveVPNConfig = async (nodeId = 0) => {
  return apiCall('/vpn/quick-connect/config', {
    method: 'POST',
    body: JSON.stringify(nodeId ? { node_id: nodeId } : {}),
  });
};

// System Admin Stats API
export const getSystemAdminStats = async () => {
  return apiCall('/admin/stats', {
    method: 'GET',
  });
};

// All Users API (System Admin)
export const getAllUsers = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.q) params.append('q', filters.q);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  
  const queryString = params.toString();
  return apiCall(`/admin/users${queryString ? `?${queryString}` : ''}`);
};

// All Companies API (System Admin)
export const getAllCompanies = async () => {
  return apiCall('/admin/companies', {
    method: 'GET',
  });
};

// System Admin Company Management API
export const getAdminCompanies = async () => {
  return apiCall('/admin/companies/list', {
    method: 'GET',
  });
};

export const getAdminCompanyInfo = async (companyId) => {
  return apiCall(`/admin/companies/${companyId}`, {
    method: 'GET',
  });
};

export const updateAdminCompany = async (companyId, companyData) => {
  return apiCall(`/admin/companies/${companyId}`, {
    method: 'PUT',
    body: JSON.stringify(companyData),
  });
};

export const updateAdminCompanyLicense = async (companyId, licenseData) => {
  return apiCall(`/admin/companies/${companyId}/license`, {
    method: 'PUT',
    body: JSON.stringify(licenseData),
  });
};

export const deleteAdminCompany = async (companyId) => {
  return apiCall(`/admin/companies/${companyId}`, {
    method: 'DELETE',
  });
};

// System Settings API (System Admin only)
export const getSystemSettings = async () => {
  return apiCall('/admin/settings', {
    method: 'GET',
  });
};

export const updateSystemSettings = async (settings) => {
  return apiCall('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify({ settings }),
  });
};

export const updateSingleSetting = async (key, value) => {
  return apiCall('/admin/settings/single', {
    method: 'PUT',
    body: JSON.stringify({ key, value }),
  });
};

export const getSystemSettingsByCategory = async (category) => {
  return apiCall(`/admin/settings/category?category=${category}`, {
    method: 'GET',
  });
};

// Downloads API (Public)
export const getPublicDownloads = async () => {
  const response = await fetch(`${API_BASE_URL}/downloads`);
  if (!response.ok) {
    throw new Error('Failed to fetch downloads');
  }
  return response.json();
};

export const incrementDownloadCount = async (downloadId) => {
  const response = await fetch(`${API_BASE_URL}/downloads/${downloadId}/count`, {
    method: 'POST',
  });
  return response.json();
};

// Downloads API (Admin)
export const getAdminDownloads = async () => {
  return apiCall('/admin/downloads');
};

export const createDownload = async (downloadData) => {
  return apiCall('/admin/downloads', {
    method: 'POST',
    body: JSON.stringify(downloadData),
  });
};

export const updateDownload = async (downloadId, downloadData) => {
  return apiCall(`/admin/downloads/${downloadId}`, {
    method: 'PUT',
    body: JSON.stringify(downloadData),
  });
};

export const deleteDownload = async (downloadId) => {
  return apiCall(`/admin/downloads/${downloadId}`, {
    method: 'DELETE',
  });
};

export const toggleDownloadVisibility = async (downloadId) => {
  return apiCall(`/admin/downloads/${downloadId}/toggle`, {
    method: 'PUT',
  });
};

// License Plans API (Public)
export const getPublicPricingPlans = async () => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(`${API_BASE_URL}/pricing/plans`, {
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error('Failed to fetch pricing plans');
    }
    return response.json();
  } finally {
    clearTimeout(timer);
  }
};

// License Plans API (Admin)
export const getAdminLicensePlans = async () => {
  return apiCall('/admin/license-plans');
};

export const updateLicensePlan = async (planId, planData) => {
  return apiCall(`/admin/license-plans/${planId}`, {
    method: 'PUT',
    body: JSON.stringify(planData),
  });
};

// ==================== TICKET SYSTEM API ====================

// Get ticket categories
export const getTicketCategories = async () => {
  return apiCall('/tickets/categories');
};

// Get user's tickets
export const getUserTickets = async (status = 'all') => {
  return apiCall(`/tickets?status=${status}`);
};

// Create a new ticket
export const createTicket = async (ticketData) => {
  return apiCall('/tickets', {
    method: 'POST',
    body: JSON.stringify(ticketData),
  });
};

// Get ticket details with messages
export const getTicketDetails = async (ticketId) => {
  return apiCall(`/tickets/${ticketId}`);
};

// Add message to a ticket
export const addTicketMessage = async (ticketId, messageData) => {
  return apiCall(`/tickets/${ticketId}/messages`, {
    method: 'POST',
    body: JSON.stringify(messageData),
  });
};

// Get attachment
export const getTicketAttachment = async (messageId) => {
  return apiCall(`/tickets/messages/${messageId}/attachment`);
};

// Admin: Get all tickets
export const getAdminTickets = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.priority) params.append('priority', filters.priority);
  if (filters.assigned) params.append('assigned', filters.assigned);
  return apiCall(`/admin/tickets?${params.toString()}`);
};

// Admin: Get ticket stats
export const getTicketStats = async () => {
  return apiCall('/admin/tickets/stats');
};

// Admin: Update ticket
export const updateTicket = async (ticketId, updateData) => {
  return apiCall(`/admin/tickets/${ticketId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
};

// Admin: Create category
export const createTicketCategory = async (categoryData) => {
  return apiCall('/admin/tickets/categories', {
    method: 'POST',
    body: JSON.stringify(categoryData),
  });
};

// Admin: Delete category
export const deleteTicketCategory = async (categoryId) => {
  return apiCall(`/admin/tickets/categories/${categoryId}`, {
    method: 'DELETE',
  });
};

export default {
  signup,
  login,
  verifyEmail,
  resendVerification,
  getProfile,
  changePassword,
  getCurrentUser,
  getLogs,
  getUsersLookup,
  createInvite,
  getInvites,
  getCompanyUsers,
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  // VPN
  getAvailableNodes,
  getAllNodesWithAccess,
  connectToNode,
  disconnectFromNode,
  getUserStats,
  getDashboardOverview,
  getUserConnections,
  getCompanyConnections,
  // Company
  getCompanyInfo,
  inviteUser,
  deleteInvite,
  // Landing Page
  getLandingContent,
  updateLandingContent,
  createLandingFeature,
  updateLandingFeature,
  deleteLandingFeature,
  createLandingTestimonial,
  updateLandingTestimonial,
  deleteLandingTestimonial,
  // Admin Nodes
  getAdminNodes,
  getConnectedAgents,
  getPendingNodes,
  approveNode,
  rejectNode,
  toggleNode,
  toggleNodeVisibility,
  updatePendingNode,
  updateNodeSettings,
  deleteNode,
  getNodeLicenses,
  getAgentRegisterKeys,
  createAgentRegisterKey,
  // System Admin
  getSystemAdminStats,
  getAllUsers,
  getAllCompanies,
  getAdminCompanies,
  getAdminCompanyInfo,
  updateAdminCompany,
  updateAdminCompanyLicense,
  deleteAdminCompany,
  // Locations
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  // System Settings
  getSystemSettings,
  updateSystemSettings,
  updateSingleSetting,
  getSystemSettingsByCategory,
  // Downloads
  getPublicDownloads,
  getAdminDownloads,
  createDownload,
  updateDownload,
  deleteDownload,
  toggleDownloadVisibility,
  incrementDownloadCount,
  // 2FA
  verify2FA,
  resend2FA,
  // License Plans
  getPublicPricingPlans,
  getAdminLicensePlans,
  updateLicensePlan,
  // Tickets
  getTicketCategories,
  getUserTickets,
  createTicket,
  getTicketDetails,
  addTicketMessage,
  getTicketAttachment,
  getAdminTickets,
  getTicketStats,
  updateTicket,
  createTicketCategory,
  deleteTicketCategory,
};
